import { Check, Copy, Fingerprint, KeyRound, ShieldCheck } from 'lucide-react';
import * as React from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router';
import {
  MfaError,
  mfa,
  securityKeys,
  verifyMfa,
  verifySecurityKey,
  type MfaChallenge,
  type MfaEnrolment,
  type Session,
} from '../accounts.ts';
import { QrCode } from '../../platform/index.ts';
import { useAuth } from '../auth.tsx';

const INPUT =
  'h-11 px-3 rounded-[5px] border text-base outline-none transition-colors disabled:opacity-50 bg-card-bg border-[var(--input-border)] text-page-text focus:border-nav-active text-center tracking-[0.3em] font-mono';
const PRIMARY =
  'h-10 rounded-[5px] text-sm font-medium text-white cursor-pointer border-0 disabled:opacity-50 disabled:cursor-not-allowed bg-nav-active hover:bg-nav-active-dark transition-colors';
const LINK =
  'text-sm text-page-muted hover:text-page-text underline bg-transparent border-0 cursor-pointer disabled:opacity-50';

// One set-up per held sign-in: a second start would replace the seed the QR code already shows (React
// runs a page's effects twice in development).
const enrolments = new Map<string, Promise<MfaEnrolment>>();
function enrolOnce(mfaToken: string) {
  let p = enrolments.get(mfaToken);
  if (!p) {
    p = mfa.enrol({ mfaToken });
    enrolments.set(mfaToken, p);
    p.catch(() => enrolments.delete(mfaToken));
  }
  return p;
}

/** What the sign-in page hands this page (router state, never the URL). */
export interface MfaState {
  mfa: MfaChallenge;
  email: string;
  /** The deep link the person was headed to before signing in. */
  from?: { pathname: string; search?: string };
}

/** Recovery codes, shown once, with a copy button; the session is adopted only after "Continue". */
export function RecoveryCodes({
  codes,
  onDone,
  doneLabel,
}: {
  codes: string[];
  onDone?: () => void;
  doneLabel?: string;
}) {
  const [copied, setCopied] = React.useState(false);
  return (
    <div className="flex flex-col gap-3" data-testid="mfa-recovery-codes">
      <p className="text-sm text-page-muted m-0">
        Save these recovery codes somewhere safe. Each one signs you in once if you lose your phone. They are
        not shown again.
      </p>
      <ol className="grid grid-cols-2 gap-x-6 gap-y-1 m-0 p-3 rounded-[6px] bg-page-bg font-mono text-sm list-none">
        {codes.map((c) => (
          <li key={c} data-testid="mfa-recovery-code">
            {c}
          </li>
        ))}
      </ol>
      <div className="flex items-center gap-2">
        <button
          type="button"
          className="inline-flex items-center gap-1.5 h-8 px-3 rounded-[5px] border border-page-divider text-sm cursor-pointer bg-card-bg text-page-text hover:border-nav-active"
          onClick={() =>
            void navigator.clipboard?.writeText(codes.join('\n')).then(
              () => setCopied(true),
              () => setCopied(false),
            )
          }
        >
          {copied ? <Check size={13} /> : <Copy size={13} />} {copied ? 'Copied' : 'Copy codes'}
        </button>
        {onDone && (
          <button
            type="button"
            className={`${PRIMARY} ml-auto px-4`}
            data-testid="mfa-continue"
            onClick={onDone}
          >
            {doneLabel ?? 'Continue'}
          </button>
        )}
      </div>
    </div>
  );
}

/**
 * Setting up an authenticator: the QR code of the otpauth URI, the manual key, and a first code that
 * must be right before two-factor is on. Used by this page (a sign-in the policy holds) and the Profile.
 */
export function EnrolAuthenticator({
  enrolment,
  busy,
  error,
  onConfirm,
}: {
  enrolment: MfaEnrolment;
  busy: boolean;
  error: string;
  onConfirm: (code: string) => void;
}) {
  const [code, setCode] = React.useState('');
  return (
    <form
      className="flex flex-col gap-4"
      data-testid="mfa-enrol"
      onSubmit={(e) => {
        e.preventDefault();
        if (code.length === 6) onConfirm(code);
      }}
    >
      <ol className="text-sm text-page-muted m-0 pl-5 flex flex-col gap-1">
        <li>
          Open an authenticator app on your phone (Google Authenticator, Microsoft Authenticator, 1Password…).
        </li>
        <li>Scan this QR code, or enter the setup key by hand.</li>
        <li>Enter the 6-digit code the app shows.</li>
      </ol>
      <div className="flex justify-center">
        <QrCode
          value={enrolment.uri}
          size={196}
          testId="mfa-qr"
          label={`Two-factor set-up for ${enrolment.account}`}
        />
      </div>
      <div className="flex flex-col gap-1">
        <span className="text-xs text-page-muted">Setup key</span>
        <code
          className="font-mono text-sm break-all px-2 py-1.5 rounded bg-page-bg text-page-text select-all"
          data-testid="mfa-secret"
        >
          {enrolment.secret.replace(/(.{4})/g, '$1 ').trim()}
        </code>
      </div>
      {error && (
        <div
          className="px-4 py-3 rounded-[5px] text-sm bg-[var(--error-bg)] text-error-text"
          data-testid="mfa-error"
          role="alert"
        >
          {error}
        </div>
      )}
      <input
        aria-label="Code from the app"
        data-testid="mfa-enrol-code"
        inputMode="numeric"
        autoComplete="one-time-code"
        maxLength={6}
        value={code}
        onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
        placeholder="000000"
        className={INPUT}
        disabled={busy}
      />
      <button
        type="submit"
        className={PRIMARY}
        disabled={busy || code.length !== 6}
        data-testid="mfa-enrol-submit"
      >
        {busy ? 'Checking…' : 'Turn on two-factor'}
      </button>
    </form>
  );
}

/**
 * /mfa-verification: the second step of a password sign-in (ADR 0014 §9), in the sign-in page's look.
 * The authenticator's 6-digit code (Verify stays off until there are six digits), or a recovery code;
 * a right code goes on to the deep link the person started from. A sign-in the organization's policy
 * holds first sets up an authenticator here and shows the recovery codes once. Opened without a
 * sign-in in progress, it goes to "/".
 */
export function MfaVerificationPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const { signedIn } = useAuth();
  const held = location.state as MfaState | null;
  const [code, setCode] = React.useState('');
  const [recovery, setRecovery] = React.useState(false);
  const keyOnly = held?.mfa.securityKeyRequired ?? false;
  // A security key first when the account has one; an organization's phishing-resistant policy accepts
  // nothing else (a recovery code still recovers a lost key).
  const [useKey, setUseKey] = React.useState(Boolean(held?.mfa.factors?.securityKey) || keyOnly);
  const [busy, setBusy] = React.useState(false);
  const [error, setError] = React.useState('');
  const [enrolment, setEnrolment] = React.useState<MfaEnrolment | null>(null);
  const [done, setDone] = React.useState<{ codes: string[]; session: Session } | null>(null);
  const enrol = held?.mfa.enrol ?? false;
  const [enrolKey, setEnrolKey] = React.useState(keyOnly);

  const back = React.useCallback(
    () =>
      navigate(`/login${location.search}`, { replace: true, state: held?.from ? { from: held.from } : null }),
    [navigate, location.search, held],
  );
  const failed = React.useCallback(
    (e: unknown) => {
      if (e instanceof MfaError && e.reason === 'challenge_expired') {
        navigate(`/login${location.search}`, {
          replace: true,
          state: { ...(held?.from ? { from: held.from } : {}), notice: e.message },
        });
        return;
      }
      setError(e instanceof MfaError ? e.message : 'Verification failed.');
    },
    [navigate, location.search, held],
  );

  React.useEffect(() => {
    if (!held?.mfa.enrol || enrolKey) return;
    let live = true;
    enrolOnce(held.mfa.mfaToken)
      .then((e) => live && setEnrolment(e))
      .catch((e: unknown) => live && failed(e));
    return () => {
      live = false;
    };
  }, [held, failed, enrolKey]);

  if (!held?.mfa?.mfaToken) return <Navigate to="/" replace />;

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError('');
    try {
      await signedIn(await verifyMfa(held.mfa.mfaToken, code.trim()));
      // The public route now sends the signed-in person on to the deep link (location.state.from).
    } catch (err) {
      failed(err);
      setCode('');
    } finally {
      setBusy(false);
    }
  };

  const confirm = async (c: string) => {
    setBusy(true);
    setError('');
    try {
      const r = await mfa.confirm({ mfaToken: held.mfa.mfaToken }, c);
      if (r.session) setDone({ codes: r.recoveryCodes, session: r.session });
    } catch (err) {
      failed(err);
    } finally {
      setBusy(false);
    }
  };

  const withKey = async () => {
    setBusy(true);
    setError('');
    try {
      await signedIn(await verifySecurityKey(held.mfa.mfaToken));
    } catch (err) {
      failed(err);
    } finally {
      setBusy(false);
    }
  };

  const addKey = async () => {
    setBusy(true);
    setError('');
    try {
      const r = await securityKeys.add({ mfaToken: held.mfa.mfaToken }, 'Security key');
      if (r.session) {
        if (r.recoveryCodes) setDone({ codes: r.recoveryCodes, session: r.session });
        else await signedIn(r.session);
      }
    } catch (err) {
      failed(err);
    } finally {
      setBusy(false);
    }
  };

  const errorBox = error && (
    <div
      className="px-4 py-3 rounded-[5px] text-sm bg-[var(--error-bg)] text-error-text"
      data-testid="mfa-error"
      role="alert"
    >
      {error}
    </div>
  );

  const ready = recovery ? code.replace(/[\s-]/g, '').length === 10 : /^\d{6}$/.test(code);

  return (
    <div className="min-h-screen flex" data-testid="mfa-page">
      <div className="md:hidden fixed top-0 left-0 right-0 h-[72px] flex items-center justify-center z-10 [background:linear-gradient(90deg,var(--nav-from)_0%,var(--nav-to)_100%)]">
        <img src="/logo.svg" alt="Logo" className="h-[40px] w-auto object-contain" />
      </div>
      <div className="flex-1 flex items-center justify-center bg-page-bg px-8 py-12 pt-[104px] md:pt-12">
        <div className="w-full max-w-[420px] flex flex-col gap-6">
          <div className="flex flex-col gap-1.5 text-center md:text-left">
            <h1 className="text-[28px] font-medium leading-tight text-page-title m-0">
              {enrol ? 'Set up two-factor authentication' : 'Two-factor verification'}
            </h1>
            <p className="text-sm text-page-muted m-0" data-testid="mfa-intro">
              {done
                ? 'Two-factor authentication is on.'
                : enrol
                  ? keyOnly
                    ? `Your organization requires a security key when ${held.email} signs in with a password.`
                    : `Your organization requires a second step when ${held.email} signs in with a password.`
                  : recovery
                    ? `Enter one of the recovery codes you saved for ${held.email}.`
                    : useKey
                      ? `Use the security key of ${held.email}.`
                      : `Enter the 6-digit code from the authenticator app for ${held.email}.`}
            </p>
          </div>

          <div className="bg-card-bg rounded-[8px] px-7 py-7 shadow-[0_4px_24px_0_var(--shadow-blue)] flex flex-col gap-5">
            {done ? (
              <RecoveryCodes codes={done.codes} onDone={() => void signedIn(done.session)} />
            ) : enrol && enrolKey ? (
              <div className="flex flex-col gap-4" data-testid="mfa-enrol-key">
                {errorBox}
                <p className="text-sm text-page-muted m-0">
                  Insert or touch your security key (or use your device’s passkey) when the browser asks.
                </p>
                <button
                  type="button"
                  className={`${PRIMARY} inline-flex items-center justify-center gap-2`}
                  disabled={busy}
                  onClick={() => void addKey()}
                  data-testid="mfa-enrol-security-key"
                >
                  <Fingerprint size={16} /> {busy ? 'Waiting for the key…' : 'Add a security key'}
                </button>
              </div>
            ) : enrol ? (
              enrolment ? (
                <EnrolAuthenticator
                  enrolment={enrolment}
                  busy={busy}
                  error={error}
                  onConfirm={(c) => void confirm(c)}
                />
              ) : error ? (
                <div
                  className="px-4 py-3 rounded-[5px] text-sm bg-[var(--error-bg)] text-error-text"
                  data-testid="mfa-error"
                  role="alert"
                >
                  {error}
                </div>
              ) : (
                <p className="text-sm text-page-muted m-0">Preparing…</p>
              )
            ) : useKey && !recovery ? (
              <div className="flex flex-col gap-5">
                {errorBox}
                <button
                  type="button"
                  className={`${PRIMARY} inline-flex items-center justify-center gap-2`}
                  disabled={busy}
                  onClick={() => void withKey()}
                  data-testid="mfa-security-key"
                  autoFocus
                >
                  <Fingerprint size={16} /> {busy ? 'Waiting for the key…' : 'Use your security key'}
                </button>
              </div>
            ) : (
              <form onSubmit={(e) => void submit(e)} className="flex flex-col gap-5">
                {error && (
                  <div
                    className="px-4 py-3 rounded-[5px] text-sm bg-[var(--error-bg)] text-error-text"
                    data-testid="mfa-error"
                    role="alert"
                  >
                    {error}
                  </div>
                )}
                <div className="flex flex-col gap-1.5">
                  <label
                    htmlFor="mfaCode"
                    className="text-sm font-medium text-page-text flex items-center gap-1.5"
                  >
                    {recovery ? <KeyRound size={14} /> : <ShieldCheck size={14} />}
                    {recovery ? 'Recovery code' : 'Verification code'}
                  </label>
                  <input
                    id="mfaCode"
                    data-testid="mfa-code"
                    value={code}
                    onChange={(e) =>
                      setCode(
                        recovery
                          ? e.target.value.slice(0, 11)
                          : e.target.value.replace(/\D/g, '').slice(0, 6),
                      )
                    }
                    placeholder={recovery ? 'xxxxx-xxxxx' : 'Enter 6-digit code'}
                    inputMode={recovery ? 'text' : 'numeric'}
                    autoComplete="one-time-code"
                    maxLength={recovery ? 11 : 6}
                    className={INPUT}
                    disabled={busy}
                    autoFocus
                    required
                  />
                </div>
                <button type="submit" disabled={busy || !ready} data-testid="mfa-verify" className={PRIMARY}>
                  {busy ? 'Verifying…' : 'Verify'}
                </button>
              </form>
            )}

            {!done && (
              <div className="flex flex-col items-center gap-2">
                {!enrol &&
                  !keyOnly &&
                  held.mfa.factors?.securityKey &&
                  held.mfa.factors.totp &&
                  !recovery && (
                    <button
                      type="button"
                      className={LINK}
                      data-testid="mfa-switch-factor"
                      onClick={() => {
                        setUseKey((k) => !k);
                        setCode('');
                        setError('');
                      }}
                      disabled={busy}
                    >
                      {useKey ? 'Use the authenticator app instead' : 'Use a security key instead'}
                    </button>
                  )}
                {enrol && !keyOnly && (
                  <button
                    type="button"
                    className={LINK}
                    data-testid="mfa-enrol-switch"
                    onClick={() => {
                      setEnrolKey((k) => !k);
                      setError('');
                    }}
                    disabled={busy}
                  >
                    {enrolKey ? 'Use an authenticator app instead' : 'Use a security key instead'}
                  </button>
                )}
                {!enrol && (
                  <button
                    type="button"
                    className={LINK}
                    data-testid="mfa-use-recovery"
                    onClick={() => {
                      setRecovery((r) => !r);
                      setCode('');
                      setError('');
                    }}
                    disabled={busy}
                  >
                    {recovery
                      ? useKey
                        ? 'Use the security key instead'
                        : 'Use the authenticator app instead'
                      : 'Use a recovery code'}
                  </button>
                )}
                <button type="button" className={LINK} data-testid="mfa-back" onClick={back} disabled={busy}>
                  Back to login
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
      <div className="hidden md:flex flex-col justify-between w-[45%] px-[60px] py-[50px] nav-sidebar-bg">
        <div className="w-[260px]">
          <img src="/logo.svg" alt="Logo" className="w-full h-auto object-contain" />
        </div>
        <div className="flex flex-col gap-5">
          <h2 className="text-[36px] font-medium leading-tight text-white">
            One more step
            <br />
            to sign in.
          </h2>
          <p className="text-sm leading-relaxed text-nav-text-dim">
            A code from your authenticator app keeps your account safe even if your password is not.
          </p>
        </div>
        <p className="text-xs text-nav-text-faint">TIVA Cortex — Business Automation Platform</p>
      </div>
    </div>
  );
}
